import { Opportunity } from "@/opportunity/opportunity";
import { useEmailRecipients, useSendNotification } from "@/opportunity/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MultiSelect, OptionType } from "@/components/ui/multi-select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Search, Send, Loader2, BellOff } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

const SendEmailDialog = ({
  opportunity,
  open,
  onOpenChange,
}: {
  opportunity: Opportunity;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { data: recipients = [] } = useEmailRecipients(opportunity.id, open);
  const sendNotification = useSendNotification(opportunity.id);
  const [selectedOrgUsers, setSelectedOrgUsers] = useState<OptionType[]>([]);
  const [selectedStaffUsers, setSelectedStaffUsers] = useState<OptionType[]>([]);
  const [selectedSearchUsers, setSelectedSearchUsers] = useState<OptionType[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const recipientLabel = (user: (typeof recipients)[number]) => {
    const name = `${user.first_name} ${user.last_name} (${user.email})`;
    if (user.is_snoozed) return `${name} — snoozed`;
    if (!user.is_opted_in) return `${name} — not opted in`;
    return name;
  };

  const orgUserOptions: OptionType[] = recipients
    .filter((user) => !!user.email && !user.is_staff)
    .map((user) => ({
      label: recipientLabel(user),
      value: user.id,
      disabled: !user.is_opted_in || user.is_snoozed,
    }));

  const staffUserOptions: OptionType[] = recipients
    .filter((user) => !!user.email && user.is_staff)
    .map((user) => ({
      label: recipientLabel(user),
      value: user.id,
      disabled: !user.is_opted_in || user.is_snoozed,
    }));

  // Search only within eligible recipients already returned by the backend
  const alreadySelectedIds = new Set([
    ...selectedOrgUsers.map((u) => u.value),
    ...selectedStaffUsers.map((u) => u.value),
  ]);

  const query = searchQuery.trim().toLowerCase();
  const searchUserOptions = (
    query.length >= 2
      ? recipients.filter((user) => {
          const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
          const email = (user.email ?? "").toLowerCase();
          return fullName.includes(query) || email.includes(query);
        })
      : []
  ).map((user) => ({
    id: user.id,
    label: recipientLabel(user),
    isSelected:
      selectedSearchUsers.some((s) => s.value === user.id) ||
      alreadySelectedIds.has(user.id),
    isSnoozed: user.is_snoozed,
    disabled: !user.is_opted_in || user.is_snoozed || alreadySelectedIds.has(user.id),
  }));

  const toggleSearchUser = (id: number, label: string) => {
    if (alreadySelectedIds.has(id)) return;
    const user = recipients.find((u) => u.id === id);
    if (!user?.is_opted_in) return;
    setSelectedSearchUsers((prev) =>
      prev.some((u) => u.value === id)
        ? prev.filter((u) => u.value !== id)
        : [...prev, { label, value: id }]
    );
  };

  const totalSelected =
    selectedOrgUsers.length +
    selectedStaffUsers.length +
    selectedSearchUsers.length;

  const handleSend = () => {
    const recipientIds = [
      ...selectedOrgUsers.map((u) => u.value as number),
      ...selectedStaffUsers.map((u) => u.value as number),
      ...selectedSearchUsers.map((u) => u.value as number),
    ];
    sendNotification.mutate(recipientIds, {
      onSuccess: (data) => {
        toast.success(
          `Email sent to ${data.sent} recipient${data.sent !== 1 ? "s" : ""}${data.failed > 0 ? ` (${data.failed} failed)` : ""}`
        );
        setSelectedOrgUsers([]);
        setSelectedStaffUsers([]);
        setSelectedSearchUsers([]);
        setSearchQuery("");
        onOpenChange(false);
      },
      onError: () => {
        toast.error("Failed to send notification email");
      },
    });
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedOrgUsers([]);
      setSelectedStaffUsers([]);
      setSelectedSearchUsers([]);
      setSearchQuery("");
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[500px]"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Send issue alert email</DialogTitle>
          <DialogDescription>
            Select recipients to notify about this issue. They will receive an
            email with the issue details and a link to the dashboard.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <Tabs defaultValue="organization">
            <TabsList className="w-full">
              <TabsTrigger value="organization" className="flex-1">
                Organization ({orgUserOptions.length})
              </TabsTrigger>
              <TabsTrigger value="staff" className="flex-1">
                Staff ({staffUserOptions.length})
              </TabsTrigger>
              <TabsTrigger value="search" className="flex-1">
                <Search className="mr-1 h-3 w-3" />
                Search
              </TabsTrigger>
            </TabsList>
            <TabsContent value="organization">
              <MultiSelect
                options={orgUserOptions}
                selected={selectedOrgUsers}
                onChange={setSelectedOrgUsers}
                placeholder="Select org recipients..."
                showSelectAllAndNone={true}
              />
            </TabsContent>
            <TabsContent value="staff">
              <MultiSelect
                options={staffUserOptions}
                selected={selectedStaffUsers}
                onChange={setSelectedStaffUsers}
                placeholder="Select staff recipients..."
                showSelectAllAndNone={true}
              />
            </TabsContent>
            <TabsContent value="search">
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <div className="max-h-48 overflow-auto rounded-md border">
                  {query.length < 2 ? (
                    <p className="py-4 text-center text-sm text-muted-foreground">
                      Type at least 2 characters to search
                    </p>
                  ) : searchUserOptions.length === 0 ? (
                    <p className="py-4 text-center text-sm text-muted-foreground">
                      No eligible users found
                    </p>
                  ) : (
                    searchUserOptions.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => toggleSearchUser(user.id, user.label)}
                        disabled={user.disabled}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent disabled:opacity-50"
                      >
                        <Checkbox
                          checked={user.isSelected}
                          disabled={user.disabled}
                          className="pointer-events-none"
                        />
                        <span className="truncate">{user.label}</span>
                        {user.isSnoozed && (
                          <BellOff size={14} className="ml-auto shrink-0 text-amber-500" />
                        )}
                        {!user.isSnoozed && alreadySelectedIds.has(user.id) && (
                          <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                            Already selected
                          </span>
                        )}
                      </button>
                    ))
                  )}
                </div>
                {selectedSearchUsers.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {selectedSearchUsers.length} user
                    {selectedSearchUsers.length !== 1 ? "s" : ""} added via
                    search
                  </p>
                )}
              </div>
            </TabsContent>
          </Tabs>
          <Button
            variant="thalo"
            onClick={handleSend}
            disabled={totalSelected === 0 || sendNotification.isPending}
          >
            {sendNotification.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Send to {totalSelected} recipient
                {totalSelected !== 1 ? "s" : ""}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SendEmailDialog;
